import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { notifyMemorialFollowers } from '../../hooks/useTributeNotifications';
import { writeFetch } from '../../api';

const {
  FiCheck, FiX, FiFlag, FiMoreVertical, FiUser, FiCheckCircle,
  FiShield, FiClock, FiFilter, FiRefreshCw, FiAlertTriangle, FiInbox
} = FiIcons;

const GIFT_CONFIG = {
  candle:  { emoji: '🕯️', label: 'Candle',  color: 'text-amber-500', bg: 'bg-amber-50',  border: 'border-amber-200' },
  flower:  { emoji: '🌹', label: 'Flowers',  color: 'text-rose-500',  bg: 'bg-rose-50',   border: 'border-rose-200'  },
  dove:    { emoji: '🕊️', label: 'Dove',    color: 'text-blue-500',  bg: 'bg-blue-50',   border: 'border-blue-200'  },
  prayer:  { emoji: '🙏', label: 'Prayer',   color: 'text-amber-600', bg: 'bg-brand-light', border: 'border-brand-dark'},
};

const SEED_QUEUE = [
  {
    id: 1, author: 'Robert Vance', relation: 'Neighbour',
    target: 'Eleanor Thompson', memorialId: 'eleanor-thompson',
    message: 'A truly wonderful soul who will be missed by everyone in the neighborhood.',
    status: 'pending', time: '12m ago', flagged: false, tributeType: 'candle', giftOption: 'gold',
    submittedAt: new Date(Date.now() - 12 * 60000).toISOString(),
  },
  {
    id: 2, author: 'Anonymous', relation: '',
    target: 'Eleanor Thompson', memorialId: 'eleanor-thompson',
    message: 'Testing the system 123.',
    status: 'pending', time: '45m ago', flagged: true, tributeType: 'prayer', giftOption: 'peace',
    submittedAt: new Date(Date.now() - 45 * 60000).toISOString(),
  },
  {
    id: 3, author: 'Clara Smith', relation: 'Former student',
    target: 'Marcus Chen', memorialId: 'marcus-chen',
    message: 'Thank you for your guidance, Captain. Rest in peace.',
    status: 'pending', time: '1h ago', flagged: false, tributeType: 'flower', giftOption: 'lily',
    submittedAt: new Date(Date.now() - 60 * 60000).toISOString(),
  },
];

const GiftBadge = ({ type }) => {
  const cfg = GIFT_CONFIG[type] || GIFT_CONFIG.candle;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${cfg.bg} ${cfg.color} ${cfg.border}`}>
      {cfg.emoji} {cfg.label}
    </span>
  );
};

const RelativeTime = ({ iso }) => {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return <span>{diff}s ago</span>;
  if (diff < 3600) return <span>{Math.floor(diff / 60)}m ago</span>;
  if (diff < 86400) return <span>{Math.floor(diff / 3600)}h ago</span>;
  return <span>{Math.floor(diff / 86400)}d ago</span>;
};

// ── Main ModerationQueue ─────────────────────────────────────────────────────
const ModerationQueue = ({ memorialFilter = null }) => {
  const [queue, setQueue] = useState(SEED_QUEUE);
  const [filter, setFilter] = useState('all');
  const [processingId, setProcessingId] = useState(null);
  const [actionLog, setActionLog] = useState([]);

  // Listen for real-time pending tribute events dispatched from SignGuestbookModal
  useEffect(() => {
    const handleNewPending = (e) => {
      const { author, relation, memorialName, memorialId, message, tributeType, giftOption } = e.detail;
      setQueue(prev => [{
        id: Date.now(),
        author: author || 'Anonymous',
        relation: relation || '',
        target: memorialName,
        memorialId,
        message,
        status: 'pending',
        flagged: false,
        tributeType: tributeType || 'candle',
        giftOption: giftOption || '',
        submittedAt: new Date().toISOString(),
        time: 'just now',
      }, ...prev]);
    };
    window.addEventListener('pending_tribute', handleNewPending);
    return () => window.removeEventListener('pending_tribute', handleNewPending);
  }, []);

  const handleApprove = async (item) => {
    setProcessingId(item.id);
    try {
      // Update DB record
      await writeFetch(`update/condolences/${item.dbId || item.id}`, {
        method: 'PUT',
        body: JSON.stringify({ is_approved: 1 }),
      }).catch(() => {});

      // Fire notification to followers now that it's approved
      await notifyMemorialFollowers({
        memorialPageId: item.memorialId,
        memorialName: item.target,
        actorName: item.author,
        tributeType: item.tributeType,
        giftType: item.giftOption,
      });

      // Dispatch so TabTributes live-updates
      window.dispatchEvent(new CustomEvent('tribute_approved', {
        detail: {
          memorialId: item.memorialId,
          tribute: {
            id: item.id, author: item.author, relation: item.relation,
            message: item.message, type: item.tributeType,
            giftOption: item.giftOption, date: item.submittedAt,
            isPrivate: false,
          },
        },
      }));

      window.dispatchEvent(new CustomEvent('memorial_activity', {
        detail: { type: item.tributeType, author: item.author, memorialId: item.memorialId, timestamp: new Date().toISOString() },
      }));

      setActionLog(prev => [{ id: Date.now(), action: 'approved', author: item.author, target: item.target }, ...prev].slice(0, 5));
      setQueue(prev => prev.filter(q => q.id !== item.id));
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (item) => {
    setProcessingId(item.id);
    try {
      await writeFetch(`update/condolences/${item.dbId || item.id}`, {
        method: 'PUT',
        body: JSON.stringify({ is_approved: -1 }),
      }).catch(() => {});

      setActionLog(prev => [{ id: Date.now(), action: 'rejected', author: item.author, target: item.target }, ...prev].slice(0, 5));
      setQueue(prev => prev.filter(q => q.id !== item.id));
    } finally {
      setProcessingId(null);
    }
  };

  const displayed = queue.filter(item => {
    if (memorialFilter && item.memorialId !== memorialFilter) return false;
    if (filter === 'flagged') return item.flagged;
    if (filter !== 'all') return item.tributeType === filter;
    return true;
  });

  const pendingCount = memorialFilter
    ? queue.filter(q => q.memorialId === memorialFilter).length
    : queue.length;

  return (
    <div className="bg-white rounded-[40px] border border-brand-dark shadow-sm overflow-hidden">
      {/* Header */}
      <div className="p-8 border-b border-brand-light">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h3 className="text-xl font-serif text-accent-navy">
              {memorialFilter ? 'Tribute Moderation' : 'Global Moderation Queue'}
            </h3>
            <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">
              {memorialFilter ? 'Review pending tributes for this memorial' : 'Platform-wide tribute review'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {pendingCount > 0 && (
              <motion.span
                key={pendingCount}
                initial={{ scale: 1.3 }}
                animate={{ scale: 1 }}
                className="bg-amber-500 text-white px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5"
              >
                <SafeIcon icon={FiClock} className="w-3 h-3" />
                {pendingCount} Pending
              </motion.span>
            )}
          </div>
        </div>

        {/* Filter pills */}
        <div className="flex flex-wrap gap-2">
          {['all', 'candle', 'flower', 'dove', 'prayer', 'flagged'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest border-2 transition-all ${filter === f ? 'bg-accent-navy border-accent-navy text-white' : 'bg-white border-brand-dark text-text-muted hover:border-accent-gold'}`}
            >
              {f === 'flagged' ? '🚩 Flagged' : f === 'all' ? 'All Types' : `${GIFT_CONFIG[f]?.emoji} ${f}s`}
            </button>
          ))}
        </div>
      </div>

      {/* Action log strip */}
      {actionLog.length > 0 && (
        <div className="px-8 py-3 bg-green-50/50 border-b border-green-100 flex items-center gap-3 overflow-x-auto">
          <SafeIcon icon={FiCheckCircle} className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
          <div className="flex gap-4">
            {actionLog.map(log => (
              <span key={log.id} className="text-[10px] font-bold whitespace-nowrap">
                <span className={log.action === 'approved' ? 'text-green-600' : 'text-red-500'}>
                  {log.action === 'approved' ? '✓ Approved' : '✗ Rejected'}
                </span>
                <span className="text-text-muted"> — {log.author} for {log.target}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Queue items */}
      <div className="divide-y divide-brand-light">
        <AnimatePresence mode="popLayout">
          {displayed.length > 0 ? displayed.map((item) => (
            <motion.div
              key={item.id}
              layout
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, x: -30, height: 0 }}
              transition={{ duration: 0.25 }}
              className={`p-6 hover:bg-brand-light/30 transition-colors group ${item.flagged ? 'border-l-4 border-red-400' : ''}`}
            >
              <div className="flex flex-col md:flex-row gap-5 items-start">
                {/* Avatar */}
                <div className="w-12 h-12 rounded-2xl bg-brand-light flex items-center justify-center text-accent-navy font-serif text-xl border border-brand-dark flex-shrink-0">
                  {item.author?.charAt(0) || '?'}
                </div>

                <div className="flex-grow min-w-0">
                  {/* Top row */}
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-bold text-accent-navy text-sm">{item.author}</h4>
                        {item.relation && (
                          <span className="text-[9px] font-black uppercase tracking-widest text-text-muted bg-brand-light px-2 py-0.5 rounded-md">
                            {item.relation}
                          </span>
                        )}
                      </div>
                      <p className="text-[10px] text-text-muted uppercase tracking-widest mt-0.5">
                        For <strong className="text-accent-navy">{item.target}</strong>
                        {' • '}
                        <RelativeTime iso={item.submittedAt} />
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <GiftBadge type={item.tributeType} />
                      {item.flagged && (
                        <span className="flex items-center gap-1 text-red-500 bg-red-50 px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest border border-red-100">
                          <SafeIcon icon={FiFlag} className="w-3 h-3" /> Auto-Flagged
                        </span>
                      )}
                      <span className="flex items-center gap-1 bg-amber-50 text-amber-600 border border-amber-200 px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest">
                        <SafeIcon icon={FiClock} className="w-3 h-3" /> Pending
                      </span>
                    </div>
                  </div>

                  {/* Message */}
                  <p className="text-sm text-text-muted font-light italic mb-4 leading-relaxed">
                    "{item.message}"
                  </p>

                  {/* Actions */}
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => handleApprove(item)}
                      disabled={processingId === item.id}
                      className="flex-grow md:flex-none px-5 py-2.5 bg-green-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-green-600 transition-colors disabled:opacity-50"
                    >
                      {processingId === item.id
                        ? <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        : <SafeIcon icon={FiCheck} className="w-3.5 h-3.5" />}
                      Approve & Publish
                    </button>
                    <button
                      onClick={() => handleReject(item)}
                      disabled={processingId === item.id}
                      className="flex-grow md:flex-none px-5 py-2.5 bg-red-50 text-red-500 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-red-100 transition-colors border border-red-100 disabled:opacity-50"
                    >
                      <SafeIcon icon={FiX} className="w-3.5 h-3.5" /> Reject
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="py-20 text-center"
            >
              <div className="w-16 h-16 bg-brand-light rounded-2xl flex items-center justify-center text-text-muted/20 mx-auto mb-4 border border-brand-dark">
                <SafeIcon icon={FiInbox} className="w-8 h-8" />
              </div>
              <p className="text-sm font-serif text-accent-navy mb-1">Queue is clear</p>
              <p className="text-xs text-text-muted">All tributes have been reviewed.</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ModerationQueue;